import React, { useState, useEffect } from 'react';
import { isVideoCallAvailableNow, getAvailableSlots } from '../../utils/videoCallUtils';
import { useTranslation } from 'react-i18next';

const VideoCallStatusBadge = ({ storeId, productId }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState('loading'); // loading, available_now, available_later, not_available, full
  const [info, setInfo] = useState(null);
  const [slotsRemaining, setSlotsRemaining] = useState(0);

  useEffect(() => {
    if (storeId) {
      checkAvailability();
    }
  }, [storeId, productId]);

  const checkAvailability = async () => {
    try {
      const result = await isVideoCallAvailableNow(storeId, productId);
      
      if (result.available) {
        setStatus('available_now');
        setInfo({
          time: t('videoCall.availableNow'),
          slotsRemaining: result.slotsRemaining || 0
        });
        setSlotsRemaining(result.slotsRemaining || 0);
      } else if (result.nextAvailable) {
        setStatus('available_later');
        setInfo({
          time: result.nextAvailable.time,
          date: result.nextAvailable.date
        });
      } else if (result.reason === 'No slots available') {
        setStatus('full');
        setInfo({
          time: result.nextAvailable?.time || 'Full'
        });
      } else {
        setStatus('not_available');
        setInfo(null);
      }

      // Also get slots info
      const slotsInfo = await getAvailableSlots(storeId);
      setSlotsRemaining(slotsInfo.availableSlots || 0);
    } catch (error) {
      console.error('Error checking video call availability:', error);
      setStatus('not_available');
    }
  };

  if (status === 'loading') {
    return (
      <div className="flex items-center gap-2 text-gray-500">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
        <span className="text-sm">{t('videoCall.checking')}</span>
      </div>
    );
  }

  if (status === 'not_available') {
    // Check if it's because schedule not set
    return (
      <div className="border rounded-lg p-3 bg-gray-50 border-gray-200">
        <div className="flex items-center gap-2">
          <span className="text-lg">⚪</span>
          <div className="flex-1">
            <div className="font-semibold text-sm text-gray-600">
              {t('videoCall.notEnabled')}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {t('videoCall.noScheduleSet')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getBadgeStyle = () => {
    switch (status) {
      case 'available_now':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'available_later':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'full':
        return 'bg-red-50 border-red-200 text-red-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getIcon = () => {
    switch (status) {
      case 'available_now':
        return '🟢';
      case 'available_later':
        return '🟡';
      case 'full':
        return '🔴';
      default:
        return '⚪';
    }
  };

  return (
    <div className={`border rounded-lg p-2 md:p-3 ${getBadgeStyle()}`}>
      <div className="flex items-center gap-2">
        <span className="text-base md:text-lg">{getIcon()}</span>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-xs md:text-sm truncate">
            {status === 'available_now' && t('videoCall.videoCallAvailable')}
            {status === 'available_later' && t('videoCall.videoCallAvailableLater')}
            {status === 'full' && t('videoCall.videoCallFull')}
          </div>
          <div className="text-xs mt-0.5 md:mt-1 line-clamp-1">
            {status === 'available_now' && (
              <>
                {t('videoCall.availableNow')}
                {slotsRemaining > 0 && (
                  <span className="ml-1">({slotsRemaining} {slotsRemaining > 1 ? t('videoCall.slots') : t('videoCall.slot')})</span>
                )}
              </>
            )}
            {status === 'available_later' && info && (
              <>
                {(() => {
                  // แปล date label (Today, Tomorrow, In X days)
                  let translatedDate = info.date;
                  if (info.date === 'Today') {
                    translatedDate = t('videoCall.today');
                  } else if (info.date === 'Tomorrow') {
                    translatedDate = t('videoCall.tomorrow');
                  } else if (info.date && info.date.startsWith('In ')) {
                    const daysMatch = info.date.match(/In (\d+) days?/);
                    if (daysMatch) {
                      const days = daysMatch[1];
                      translatedDate = t('videoCall.inDays', { days });
                    }
                  }
                  return t('videoCall.nextAvailable', { date: translatedDate, time: info.time });
                })()}
              </>
            )}
            {status === 'full' && (
              <>{t('videoCall.noSlotsAvailable')}</>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoCallStatusBadge;

